/**
 * 파일명 : usr0201m.jsx
 * 설명 : 생일현황
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2023-04-04   윤성수     최초작성
 * 1.1       2024-03-12   방진선     포인트 기능 삭제
 *     
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0201mAction } from '../../../redux/slice/usr/usr0201m_slice';
import { IInquiryCndsState, IUseBirdState } from '../../../redux/models/usr/usr0201m_model';

/*  AntD  */
import { Button, Space, Row, Col, Layout, Typography, Collapse, DatePicker, ConfigProvider, Table} from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/*  업무  */
import ExportExcelFile from 'components/exportExcelFile';
import dayjs from 'dayjs';
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';

/************************************************
 * 전역변수 영역
 ************************************************/
const Usr0201m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const useBirdList = useAppSelector((state) => state.Usr0201m.useBirdList);
    const inquiryCnds = useAppSelector((state) => state.Usr0201m.inquiryCnds);
    const current     = useAppSelector((state) => state.Usr0201m.current);
    const pageSize    = useAppSelector((state) => state.Usr0201m.pageSize);

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        const today = new Date();

        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const formattedDate = `${year}-${month}`;
        
        const initInquiryCnds =  {
            baseYm : formattedDate 
        };
        
        dispatch(Usr0201mAction.setInquiryCnds(initInquiryCnds));

        fn_Search(initInquiryCnds);
    }, []);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;


    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>번호</div>,
                dataIndex: 'no',
                key: 'no',
                align: 'center',
                width: '5%',
                render: (_: any, __: IUseBirdState, index: number) => (current - 1) * pageSize + index + 1,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직원명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'center',
                width: 120,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: 80,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'left',
                width: 150,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>생일</div>,
                dataIndex: 'birdDt',
                key: 'birdDt',
                align: 'center',
                width: 120,
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>구분</div>,
                dataIndex: 'birdTpNm',
                key: 'birdTpNm',
                align: 'center',
                width: 80,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>대상일자</div>,
                dataIndex: 'baseDt',
                key: 'baseDt',
                align: 'center',
                width: 120,
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
            }];
            return arr;
    }, [current, pageSize]);

    /* 엑셀다운로드 */
    const excelParams = {
        datas: useBirdList,
        btnName: '엑셀파일 내려받기',
        fileName: '생일현황_' + inquiryCnds.baseYm,
        sheetName: '생일현황_' + inquiryCnds.baseYm,
        columns: columns(),
    }

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Usr0201mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Usr0201mAction.setUserBirdList([]));
        dispatch(Usr0201mAction.setCurrent(1));
    };

    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const calendar_onChange = (_:any, dateStrings:any) => {
        dispatch(Usr0201mAction.setInquiryCnds({ baseYm : dateStrings }));
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = (searchCnds?: IInquiryCndsState) => {
        dispatch(Usr0201mAction.setCurrent(1));

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...(searchCnds ? searchCnds : inquiryCnds)
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0007',
            success: (responseData:any) => {
                dispatch(Usr0201mAction.setUserBirdList(responseData.userBirdPoints));
            },
        });
    };

    return (
        <>
            <Layout>
                <Content>
                    <Collapse   
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Col style={{ paddingLeft: 20 }}>
                                    <Space>
                                        <label>조회기간</label>
                                        <ConfigProvider locale={locale}>
                                            <DatePicker
                                                style={{ width: 120 }}
                                                id='tx_calendar'
                                                picker='month'
                                                allowClear={false}
                                                value={dayjs(inquiryCnds.baseYm)}
                                                onChange={calendar_onChange}
                                            />
                                        </ConfigProvider>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={onClick}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={onClick}
                                        >
                                            조회
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col flex='9.5'>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>생일현황</Text>
                            </Space>
                        </Col>
                        <Col flex='0.5'>
                            <Space>
                                <ExportExcelFile excelParams={excelParams}/>
                            </Space>
                        </Col>
                    </Row>
                    <Table
                        dataSource={useBirdList}
                        key='empNo'
                        columns={columns()}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + useBirdList.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Usr0201mAction.setCurrent(page));
                                dispatch(Usr0201mAction.setPageSize(pageSize));
                            },
                        }}
                    />
                </Content>
            </Layout>
        </>
    );
};

export default Usr0201m;